import { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";

import { useMediaQuery, Typography } from "@mui/material";
import { useSignupMutation } from "../../features/auth/authApiSlice";
import {
  useSendOTPMutation,
  useVerifyOTPMutation,
} from "../../features/auth/apiSlice";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  contact: "",
  password: "",
  cnfPassword: "",
  otp: "",
  address: "",
  businessCategory: "",
  businessName: "",
};
const phoneRegExp = /^[6-9]\d{9}$/;

const signupSchema = yup.object().shape({
  firstName: yup.string().required("required"),
  lastName: yup.string().required("required"),
  email: yup.string().email("invalid email").required("required"),
  contact: yup
    .string()
    .matches(phoneRegExp, "Phone number is not valid")
    .required("required"),
  password: yup.string().required("required"),
  cnfPassword: yup.string().required("required"),
  address: yup.string().required("required"),
  businessCategory: yup.string().required("required"),
  businessName: yup.string().required("required"),
});

interface OTP {
  hash: string;
  otp: string;
}

const Index = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [error, setError] = useState("");
  const [signup, { isLoading }] = useSignupMutation();
  const navigate = useNavigate();
  const [sendOTPAPI] = useSendOTPMutation();
  const [email, setEmail] = useState("");
  const [showOtpField, setShowOtpField] = useState(false);
  const [verifyOTPAPI] = useVerifyOTPMutation();
  const [enableSignup, setEnableSignup] = useState(false);
  const [otp, setOTP] = useState({} as OTP);
  const [otpError, setOtpError] = useState("");

  const handleFormSubmit = async (values: any) => {
    try {
      if (values.password !== values.cnfPassword) {
        throw new Error("Password not matched");
      }
      await signup({
        ...values,
        name: values.firstName + " " + values.lastName,
      });
      navigate("/");
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRequestOTP = async () => {
    if (email === "") {
      return setError("Email is Required");
    }
    const response: any = await sendOTPAPI({ email }).unwrap();
    if (response.status === 200) {
      setShowOtpField(true);
      setOTP({ ...otp, hash: response.hash });
    }
  };

  const verifyOTP = async () => {
    const response: any = await verifyOTPAPI({
      otp: otp.otp,
      hash: otp.hash,
      email: email,
    }).unwrap();
    if (response.isOtpVerified) {
      setEnableSignup(true);
      setOtpError("");
    } else {
      setOtpError("OTP is Invalid!");
    }
  };

  return (
    <div className="flex items-center justify-center mt-[50px]">
      {isLoading ? (
        <div className="flex items-center justify-center">
          <h1>Loading...</h1>
        </div>
      ) : (
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={signupSchema}
        >
          {/* @ts-ignore */}
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Box
                  display={"grid"}
                  gap="10px"
                  gridTemplateColumns={"repeat(4, minmax(0,1fr))"}
                  sx={{
                    "& > div": {
                      gridColumn: isNonMobile ? undefined : "span 4",
                    },
                  }}
                >
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="First Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName}
                    name="firstName"
                    error={!!touched.firstName && !!errors.firstName}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="lastName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName}
                    name="lastName"
                    error={!!touched.lastName && !!errors.lastName}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="contact"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.contact}
                    name="contact"
                    error={!!touched.contact && !!errors.contact}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="Email"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                      setEmail(e.target.value);
                    }}
                    value={values.email}
                    name="email"
                    error={!!touched.email && !!errors.email}
                    sx={{ gridColumn: "span 4" }}
                  />
                  {!showOtpField ? (
                    <Button
                      color="secondary"
                      variant="contained"
                      onClick={handleRequestOTP}
                    >
                      Request OTP
                    </Button>
                  ) : (
                    <>
                      <TextField
                        variant="outlined"
                        type={"text"}
                        label="OTP"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          handleChange(e);
                          setOTP({ ...otp, otp: e.target.value });
                        }}
                        value={values.otp}
                        name="otp"
                        error={!!touched.otp && !!errors.otp}
                        sx={{ gridColumn: "span 4" }}
                      />
                      {enableSignup ? (
                        <Typography variant="h5" color={"green"}>
                          OTP Verified
                        </Typography>
                      ) : (
                        <Button
                          onClick={verifyOTP}
                          variant="contained"
                          color="secondary"
                        >
                          Verify OTP
                        </Button>
                      )}
                      {otpError && (
                        <Typography variant="h6" color={"red"}>
                          {otpError}
                        </Typography>
                      )}
                    </>
                  )}
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="Address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.address}
                    name="address"
                    error={!!touched.address && !!errors.address}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="Business Category"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.businessCategory}
                    name="businessCategory"
                    error={
                      !!touched.businessCategory && !!errors.businessCategory
                    }
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"text"}
                    label="Business Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.businessName}
                    name="businessName"
                    error={!!touched.businessName && !!errors.businessName}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"password"}
                    label="Password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    name="password"
                    error={!!touched.password && !!errors.password}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    variant="outlined"
                    type={"password"}
                    label="Confirm Password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.cnfPassword}
                    name="cnfPassword"
                    error={!!touched.cnfPassword && !!errors.cnfPassword}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    sx={{ gridColumn: "span 4" }}
                    style={
                      !enableSignup
                        ? { background: "#cccccc", color: "#666666" }
                        : {}
                    }
                    disabled={!enableSignup}
                  >
                    Signup
                  </Button>
                </Box>
                <Typography variant="h4" align="center" color={"red"}>
                  {error}
                </Typography>
                <div className="mt-5 flex w-[100%] justify-between items-center">
                  <div className="flex-1">
                    <hr />
                  </div>
                  <div>OR</div>
                  <div className="flex-1">
                    <hr />
                  </div>
                </div>
                <div className="mt-5 flex justify-center">
                  <Typography variant="h5" color={"blue"}>
                    <Link to="/" className="underline">
                      Already a sponser?
                    </Link>
                  </Typography>
                </div>
              </form>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default Index;
